import React from 'react'
import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import PublishUtils from '../../../helpers/PublishUtils'

export const configOptions = [
  {
    name: 'shapeQuery',
    type: 'yasgui',
    label: (
      <span>
        SPARQL CONSTRUCT query to define SHACL shapes for the form<br />
        <strong>The results are combined with the results from Turtle</strong>
      </span>
    ),
    defaultQuery: ``,
  },
  {
    name: 'shapeTtl',
    type: 'turtle',
    label: (
      <span>
        Turtle to define SHACL shapes for the form<br />
        <strong>The results are combined with results from the CONSTRUCT query</strong>
      </span>
    ),
  },
  {
    name: 'graph',
    type: 'text',
    label: 'Graph parameter to store the data'
  },
  {
    name: 'uriPrefix',
    type: 'text',
    label: 'URI prefix for normalizing IRIs without scheme (e.g., "https://wistor.nl/resource/")',
  },
  {
    name: 'dataQuery',
    type: 'yasgui',
    label: (
      <span>
      SPARQL CONSTRUCT query to load data into the form for editing<br />
      <strong>Leave empty to create a new resource</strong>
    </span>
    ),
    defaultQuery: ``,
  },
  {
    name: 'n3Rules',
    type: 'turtle',
    label: (
      <span>
        <strong>N3 Rules</strong>
        <br />
        N3 rules to execute before form submission. Rules are applied to the data using N3.js reasoning.
      </span>
    ),
  },
  {
    name: 'successMessage',
    type: 'text',
    label: 'Success message to display after form submission',
  },
  {
    name: 'failureMessage',
    type: 'text',
    label: 'Failure message to display on submission error',
  },
  {
    name: 'closePopup',
    type: 'boolean',
    label: 'Close popup/dialog after successful submission',
  },
  {
    name: 'ruleToTriggerAfterSubmission',
    type: 'text',
    label: 'Wistor rule to trigger after successful submission (tag or URI)',
  },
  {
    name: 'ruleParameters',
    type: 'json-text',
    label: 'Parameters for the rule (JSON format)',
  },
  {
    name: 'submitButtonTitle',
    type: 'text',
    label: 'Submit button text',
  },
  {
    name: 'submitButtonBeginIcon',
    type: 'text',
    label: 'Submit button begin icon - available prefixes: `fa-` `mui-` `radix-`',
  },
  {
    name: 'submitButtonEndIcon',
    type: 'text',
    label: 'Submit button end icon - available prefixes: `fa-` `mui-` `radix-`',
  },
  {
    name: 'submitButtonCustomStyling',
    type: 'boolean',
    label: 'Enable custom styling for submit button',
  },
  {
    name: 'submitButtonBorderRadius',
    type: 'text',
    label: 'Custom Styling: button-border-radius (examples: "2lh", "50%", "1.2em")',
  },
  {
    name: 'submitButtonTextColor',
    type: 'text',
    label: 'Custom Styling: text-color (examples: "white", "#FFFFFF")',
  },
  {
    name: 'submitButtonBgColor',
    type: 'text',
    label: 'Custom Styling: background-color (examples: "blue", "#007bff")',
  },
  {
    name: 'submitButtonHasShadow',
    type: 'boolean',
    label: 'Custom Styling: enable button shadow',
  },
  {
    name: 'submitButtonFontSize',
    type: 'text',
    label: 'Custom Styling: button-font-size',
  },
  {
    name: 'submitButtonFontFamily',
    type: 'text',
    label: 'Custom Styling: button-font-family',
  },
  {
    name: 'language',
    type: 'text',
    label: (
      <span>
        <strong>data-language:</strong> Language code for langStrings (e.g., "en", "de")<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'viewMode',
    type: 'boolean',
    label: (
      <span>
        <strong>data-view:</strong> Enable read-only viewer mode (Only works for default theme for now)<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'collapseMode',
    type: 'text',
    label: (
      <span>
        <strong>data-collapse:</strong> Collapse groups/nested properties ("" = collapsed, "open" = expanded)<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'dense',
    type: 'boolean',
    label: (
      <span>
        <strong>data-dense:</strong> Compact rendering mode (default: true)<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'hierarchyColors',
    type: 'text',
    label: (
      <span>
        <strong>data-hierarchy-colors:</strong> Comma-separated CSS colors for nesting levels<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'showNodeIds',
    type: 'boolean',
    label: (
      <span>
        <strong>data-show-node-ids:</strong> Display SHACL node shape subject IDs<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'showRootShapeLabel',
    type: 'boolean',
    label: (
      <span>
        <strong>data-show-root-shape-label:</strong> Display root shape heading<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'loadingText',
    type: 'text',
    label: (
      <span>
        <strong>data-loading:</strong> Text shown during form initialization<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'ignoreOwlImports',
    type: 'boolean',
    label: (
      <span>
        <strong>data-ignore-owl-imports:</strong> Disable automatic owl:imports fetching<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'valuesNamespace',
    type: 'text',
    label: (
      <span>
        <strong>data-values-namespace:</strong> RDF namespace for new subjects<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'valuesGraph',
    type: 'text',
    label: (
      <span>
        <strong>data-values-graph:</strong> Named graph IRI for created data<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'generateNodeShapeReference',
    type: 'text',
    label: (
      <span>
        <strong>data-generate-node-shape-reference:</strong> Predicate for shape conformance triple<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'proxyUrl',
    type: 'text',
    label: (
      <span>
        <strong>data-proxy:</strong> Proxy URL for CORS-restricted resources<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#element-attributes" target="_blank" rel="noopener noreferrer">shacl-form Element Attributes</a></small>
      </span>
    ),
  },
  {
    name: 'theme',
    type: 'select',
    label: (
      <span>
        <strong>Theme:</strong> Visual theme for the form<br />
        <small>Docs: <a href="https://github.com/ULB-Darmstadt/shacl-form?tab=readme-ov-file#theming" target="_blank" rel="noopener noreferrer">shacl-form Theming</a></small>
      </span>
    ),
    options: [
      { value: '', label: 'Default (Library)' },
      { value: 'material-design', label: 'Modern Design' },
    ],
  },
  PublishUtils.getTimestampParametersDef()
] as const

export type ShaclFormConfigOptions = ConfigurablePropsFromDescription<typeof configOptions>
